import type { Record } from "@suse-o11y/record";
import isFunction from "lodash/isFunction";
import qs from "query-string";

import type { ExtractPathParams } from "../link";

import { type SmartHistoryLocation, urlToSmartHistoryLocation } from "./SmartHistoryLocation";
import { SmartHistoryQueueEntry } from "./SmartHistoryQueueEntry";
import type {
	SmartHistoryRoute,
	SmartHistoryRoutePayload,
	SmartHistoryRoutePayloadExtractor,
} from "./SmartHistoryRoute";

/**
 * A pattern is a set of instructions how to convert a payload of a specific path to a result.
 */
export type SmartHistorySchemaPattern<TSchema, TResult> = {
	_: () => TResult;
} & {
	[TPath in keyof TSchema]: (payload: TSchema[TPath]) => TResult;
};

export interface SmartHistorySchemaOptions<TSearchParams> {
	/**
	 * A decoder for search params for building an entry's payload.
	 *
	 * @default `parse` from "query-string"
	 */
	decodeSearchParams?(search: string): TSearchParams;
}

/**
 * A schema that specifies how to convert a location to a payload by matching it against a list of routes.
 * The schema is used to fill in the SmartHistoryQueue with only relevant entries.
 */
export class SmartHistorySchema<
	TSchema extends Record<string, SmartHistoryRoutePayload> = never,
	TSearchParams extends Record<string> = Record<string>,
> {
	private readonly routes: Array<SmartHistoryRoute> = [];
	private readonly options: SmartHistorySchemaOptions<TSearchParams>;

	public constructor(options: SmartHistorySchemaOptions<TSearchParams> = {}) {
		this.options = options;
	}

	/**
	 * Registers a route to the schema.
	 *
	 * @param path a string to match against a location.
	 * @param extract a function to convert the matched location params and search params to the payload.
	 *
	 * @returns the schema itself for easy chaining that helps to automatically infer the TSchema type.
	 */
	public route<TPath extends string, TPayload extends SmartHistoryRoutePayload = never>(
		path: TPath,
		extract?: SmartHistoryRoutePayloadExtractor<
			ExtractPathParams<TPath, { parseParamValues: false }>,
			TSearchParams,
			TPayload
		>,
	): SmartHistorySchema<
		[TSchema] extends [never] ? Record<TPath, TPayload> : TSchema & Record<TPath, TPayload>,
		TSearchParams
	> {
		this.routes.push({
			path,
			extract: extract as SmartHistoryRoute["extract"],
		});

		return this as SmartHistorySchema<TSchema & Record<TPath, TPayload>, TSearchParams>;
	}

	/**
	 * Matches a location against the schema's routes.
	 * All routes are strictly (only full-path match) matched in the order they were registered.
	 * The first match wins.
	 *
	 * @returns if the location matches a route, returns an entry with the payload, otherwise null.
	 */
	public match(location: SmartHistoryLocation): null | SmartHistoryQueueEntry {
		const searchParams = isFunction(this.options.decodeSearchParams)
			? this.options.decodeSearchParams(location.search)
			: qs.parse(location.search);

		for (const route of this.routes) {
			const entry = SmartHistoryQueueEntry.match(location, route, searchParams);

			if (entry != null) {
				return entry;
			}
		}

		return null;
	}

	/**
	 * Converts an url to a result by matching it against the schema's routes.
	 * TypeScript makes sure that all specified routes are listed in the pattern.
	 *
	 * @returns if the url matches a route, returns the result, otherwise the default `pattern._()` result.
	 */
	public cata<TResult>(url: string, pattern: SmartHistorySchemaPattern<TSchema, TResult>): TResult {
		const entry = this.match(urlToSmartHistoryLocation(url));

		if (entry == null) {
			return pattern._();
		}

		return entry.resolve(pattern);
	}
}
